import { useDocumentVisibility } from '@vueuse/core';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

import {
  useEnterOrganizationAsOwnerMutation as useGeneratedEnterOrganizationAsOwnerMutation,
  useForceStopPlatformImpersonationSessionMutation as useGeneratedForceStopPlatformImpersonationSessionMutation,
  useGetPlatformActivityFeedQuery,
  useGetPlatformImpersonationActionsQuery,
  useGetPlatformImpersonationSessionsQuery,
  useGetPlatformOrganizationQuery,
  useGetPlatformOrganizationsQuery,
  useGetPlatformOverviewQuery,
  useGetPlatformQueueStatsQuery,
  useGetPlatformUserQuery,
  useGetPlatformUsersQuery,
  useSetPlatformOrganizationModulesMutation as useGeneratedSetPlatformOrganizationModulesMutation,
  useStartPlatformImpersonationMutation as useGeneratedStartPlatformImpersonationMutation,
  useStopPlatformImpersonationMutation as useGeneratedStopPlatformImpersonationMutation,
} from '@shared/api/graphql/generated';

import { platformContext, platformMutationContext } from './platform-context';

export const usePlatformOrganizationsQuery = (
  variables: Parameters<typeof useGetPlatformOrganizationsQuery>[0],
  enabled: MaybeRefOrGetter<boolean> = true,
) =>
  useGetPlatformOrganizationsQuery(
    variables,
    computed(() => ({ ...platformContext, enabled: toValue(enabled) })),
  );

export const usePlatformUsersQuery = (
  variables: Parameters<typeof useGetPlatformUsersQuery>[0],
  enabled: MaybeRefOrGetter<boolean> = true,
) =>
  useGetPlatformUsersQuery(
    variables,
    computed(() => ({ ...platformContext, enabled: toValue(enabled) })),
  );

export const usePlatformUserQuery = (variables: Parameters<typeof useGetPlatformUserQuery>[0]) =>
  useGetPlatformUserQuery(variables, platformContext);

export const usePlatformOrganizationQuery = (variables: Parameters<typeof useGetPlatformOrganizationQuery>[0]) =>
  useGetPlatformOrganizationQuery(variables, platformContext);

export const usePlatformImpersonationSessionsQuery = (
  variables: Parameters<typeof useGetPlatformImpersonationSessionsQuery>[0],
) => useGetPlatformImpersonationSessionsQuery(variables, platformContext);

export const usePlatformImpersonationActionsQuery = (
  variables: Parameters<typeof useGetPlatformImpersonationActionsQuery>[0],
  enabled: MaybeRefOrGetter<boolean>,
) =>
  useGetPlatformImpersonationActionsQuery(
    variables,
    computed(() => ({
      ...platformContext,
      enabled: toValue(enabled),
    })),
  );

/**
 * Опрос штаба. data-refresh runtime работает только внутри организации и на hq
 * ничего не перечитает, поэтому интервал задаётся здесь; скрытая вкладка не опрашивает.
 */
const usePlatformPolling = (intervalMs: number) => {
  const visibility = useDocumentVisibility();

  return computed(() => ({
    ...platformContext,
    fetchPolicy: 'cache-and-network' as const,
    pollInterval: visibility.value === 'visible' ? intervalMs : 0,
  }));
};

export const usePlatformOverviewQuery = (variables: Parameters<typeof useGetPlatformOverviewQuery>[0]) =>
  useGetPlatformOverviewQuery(variables, usePlatformPolling(60_000));

export const usePlatformActivityFeedQuery = (variables: Parameters<typeof useGetPlatformActivityFeedQuery>[0]) =>
  useGetPlatformActivityFeedQuery(variables, usePlatformPolling(5_000));

export const usePlatformQueueStatsQuery = () => useGetPlatformQueueStatsQuery(usePlatformPolling(30_000));

export const useStartPlatformImpersonationMutation = () =>
  useGeneratedStartPlatformImpersonationMutation(platformMutationContext);

export const useStopPlatformImpersonationMutation = () =>
  useGeneratedStopPlatformImpersonationMutation(platformMutationContext);

export const useEnterOrganizationAsOwnerMutation = () =>
  useGeneratedEnterOrganizationAsOwnerMutation(platformMutationContext);

export const useForceStopPlatformImpersonationSessionMutation = () =>
  useGeneratedForceStopPlatformImpersonationSessionMutation(platformMutationContext);

export const useSetPlatformOrganizationModulesMutation = () =>
  useGeneratedSetPlatformOrganizationModulesMutation(platformContext);
